// app/javascript/controllers/sub_industry_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "options", "option", "hiddenInput"]
  static values = {
    industry: String,
    selected: String,
    defaultText: String,
    locale: String
  }

  connect() {
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.closeOnClickOutside)
    if (this.industryValue) {
      this.updateOptions(this.industryValue, this.selectedValue)
    } else {
      this.disable()
    }
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnClickOutside)
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    if (this.buttonTarget.disabled) return

    event.stopPropagation()
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.optionsTarget.classList.remove("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.optionsTarget.classList.add("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  isOpen() {
    return !this.optionsTarget.classList.contains("hidden")
  }

  disable() {
    this.buttonTarget.disabled = true
    this.buttonTarget.classList.remove('cursor-pointer', 'bg-white', 'text-gray-900', 'outline-gray-300')
    this.buttonTarget.classList.add('cursor-not-allowed', 'text-gray-500', 'outline-gray-200')
    this.resetDropdown()
  }

  enable() {
    this.buttonTarget.disabled = false
    this.buttonTarget.classList.remove('cursor-not-allowed', 'bg-gray-50', 'text-gray-500', 'outline-gray-200')
    this.buttonTarget.classList.add('cursor-pointer', 'bg-white', 'text-gray-900', 'outline-gray-300')
  }

  resetDropdown() {
    this.buttonTarget.querySelector("span").textContent = this.defaultTextValue || this.buttonTarget.querySelector("span").textContent
    this.hiddenInputTarget.value = ""
    this.optionsTarget.innerHTML = ""
  }

  async updateOptions(industryId, selectedSubIndustryId = null) {
    if (!industryId) {
      this.disable()
      return
    }

    try {
      // URL'e locale parametresini ekleyelim
      const response = await fetch(`/industries/${industryId}/sub_industries?locale=${this.localeValue}`)
      if (!response.ok) throw new Error('Network response was not ok')

      const subIndustries = await response.json()
      this.populateOptions(subIndustries, selectedSubIndustryId)
      this.enable()

      if (selectedSubIndustryId) {
        const selectedSubIndustry = subIndustries.find(si => si.id === selectedSubIndustryId)
        if (selectedSubIndustry) {
          this.buttonTarget.querySelector("span").textContent = selectedSubIndustry.name
          this.hiddenInputTarget.value = selectedSubIndustryId
        }
      }
    } catch (error) {
      console.error("Error loading sub-industries:", error)
      this.disable()
    }
  }

  populateOptions(subIndustries, selectedSubIndustryId = null) {
    // "All Sub Industries" seçeneğini en üstte ekleyelim
    const allSubIndustriesOption = `
      <li class="relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none hover:bg-[#0352ff] hover:text-white group"
          data-sub-industry-target="option"
          data-action="click->sub-industry#select"
          data-value=""
          role="option"
          ${!selectedSubIndustryId ? 'aria-selected="true"' : ''}>
        <span class="block truncate ${!selectedSubIndustryId ? 'font-semibold' : 'font-normal'}">
          ${this.defaultTextValue}
        </span>
        <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff] group-hover:text-white ${!selectedSubIndustryId ? '' : 'hidden'}">
          <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
          </svg>
        </span>
      </li>
    `;

    const subIndustriesHtml = subIndustries.map(subIndustry => `
      <li class="relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none hover:bg-[#0352ff] hover:text-white group"
          data-sub-industry-target="option"
          data-action="click->sub-industry#select"
          data-value="${subIndustry.id}"
          role="option"
          ${selectedSubIndustryId === subIndustry.id ? 'aria-selected="true"' : ''}>
        <span class="block truncate ${selectedSubIndustryId === subIndustry.id ? 'font-semibold' : 'font-normal'}">
          ${subIndustry.name}
        </span>
        <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff] group-hover:text-white ${selectedSubIndustryId === subIndustry.id ? '' : 'hidden'}">
          <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
          </svg>
        </span>
      </li>
    `).join('')

    // "All Sub Industries" seçeneğini ve diğer seçenekleri birleştir
    this.optionsTarget.innerHTML = allSubIndustriesOption + subIndustriesHtml
  }



  select(event) {
    const option = event.currentTarget
    const value = option.dataset.value
    const text = option.querySelector("span:first-child").textContent

    this.hiddenInputTarget.value = value
    this.buttonTarget.querySelector("span").textContent = text

    this.element.querySelectorAll('[data-sub-industry-target="option"]').forEach(opt => {
      opt.setAttribute('aria-selected', 'false')
      opt.querySelector("span:first-child").classList.remove('font-semibold')
      opt.querySelector("span:first-child").classList.add('font-normal')
      opt.querySelector("span:last-child").classList.add("hidden")
    })

    option.setAttribute('aria-selected', 'true')
    option.querySelector("span:first-child").classList.remove('font-normal')
    option.querySelector("span:first-child").classList.add('font-semibold')
    option.querySelector("span:last-child").classList.remove("hidden")

    this.close()
  }
}
